import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';

interface VerifiedBadgeProps {
  className?: string;
  size?: 'sm' | 'md';
  /**
   * Visual variant of the badge.
   *  - `default`  → blue verified tick (standard verified user)
   *  - `premium`  → golden tick (used when `isPremiumUser` is true)
   */
  variant?: 'default' | 'premium';
}

const sizeClassNames = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
};

const iconSizeClassNames = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
};

const variantClassNames = {
  default: 'bg-amber-400 text-white',
  // Rich gold gradient + subtle glow to differentiate premium users.
  premium:
    'bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-600 text-white shadow-[0_0_8px_rgba(234,179,8,0.45)]',
};

export default function VerifiedBadge({
  className,
  size = 'md',
  variant = 'default',
}: VerifiedBadgeProps) {
  const { t } = useTranslation('common');
  const isPremium = variant === 'premium';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full ring-2 ring-white',
        sizeClassNames[size],
        variantClassNames[variant],
        className,
      )}
      title={isPremium ? t('premiumMember') : t('verified')}
      aria-label={isPremium ? t('premiumAccount') : t('verifiedAccount')}
    >
      <CheckIcon className={iconSizeClassNames[size]} strokeWidth={3} />
    </span>
  );
}
